import React, { useState } from 'react';
import NumericKeypad from './NumericKeypad';
import { stockChoiceSummary } from './StockRecipeChoices';

export const MAYTHAI_DEMO_UUID = /^[a-f0-9]{8}-[a-f0-9]{4}-4[a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/i;
const uuid = () => window.crypto.randomUUID();
const money = cents => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(cents / 100);
const productDetails = product => [stockChoiceSummary(product), ...(product.selectedSupplements || []).map(item => `Supplément : ${item.name}`)].filter(Boolean);
const boundedDetails = details => details.flatMap(detail => {
  const text = String(detail);
  return Array.from({ length: Math.ceil(text.length / 500) }, (_, index) => text.slice(index * 500, (index + 1) * 500));
});

// A browser-only demonstration. This module deliberately has no API, storage,
// customer identity, payment provider or printer dependency.
export function createMaythaiDemoRuntime({ catalogue, sessionId, onNavigate, onConfirm, now = () => Date.now() }) {
  if (!MAYTHAI_DEMO_UUID.test(sessionId || '')) throw new Error('Session de démonstration invalide.');
  let ticketNumber = 0;
  return {
    catalogue, sessionId, onNavigate,
    confirm({ cart, total, paymentMethod, comment = '', includeCutlery = false }) {
      if (!['cb', 'espece'].includes(paymentMethod) || !Array.isArray(cart) || !cart.length || cart.length > 200) throw new Error('Choisis un panier et un mode de paiement.');
      const totalCents = Math.round(Number(total) * 100);
      if (!Number.isSafeInteger(totalCents) || totalCents < 0 || totalCents > 1000000) throw new Error('Montant de démonstration invalide.');
      const items = cart.map(item => {
        const details = item.isMenu ? (item.customSelections || []).flatMap(section => {
          const selected = section.products || (section.product ? [section.product] : []);
          return [`${section.sectionName} : ${selected.map(product => product.name).join(' · ')}`,
            ...selected.flatMap(product => productDetails(product).map(detail => `${product.name} : ${detail}`))];
        }) : productDetails(item);
        if (typeof item.name !== 'string' || !item.name.trim() || item.name.length > 300 || !Number.isSafeInteger(item.quantity) || item.quantity < 1 || item.quantity > 99) throw new Error('Panier de démonstration invalide.');
        return { name: item.name, quantity: item.quantity, details };
      });
      if (comment.trim()) items[items.length - 1].details.push(`Commentaire : ${comment.trim().slice(0, 500)}`);
      if (includeCutlery) items[items.length - 1].details.push('Avec couverts');
      for (const item of items) {
        item.details = boundedDetails(item.details);
        if (item.details.length > 50) throw new Error('Cette composition dépasse la taille du ticket de démonstration.');
      }
      const time = now();
      const order = { id: uuid(), ticketNumber: ++ticketNumber, totalCents, paymentMethod, paymentStatus: 'demo',
        createdAt: new Date(time).toISOString(), expiresAt: new Date(time + 24 * 60 * 60 * 1000).toISOString(), items };
      onConfirm?.(order);
      return order;
    },
  };
}

export function MaythaiDemoIdentity({ onContinue, onClose }) {
  const [mode, setMode] = useState('');
  const [phone, setPhone] = useState('');
  const [sent, setSent] = useState(false);
  return <div className="maythai-demo-identity">
    {sent ? <div role="status"><h2>Le SMS est envoyé</h2><p>Ton lien {mode === 'register' ? 'd’inscription' : 'de connexion'} a été envoyé au <strong>{phone.replace(/(\d{2})(?=\d)/g, '$1 ')}</strong>.</p>
      <p className="maythai-demo-note">Simulation : aucun SMS n’est envoyé et aucun compte n’est créé.</p>
      <button type="button" className="kiosk-cta" onClick={onContinue}>Continuer ma commande</button>
      <button type="button" className="kiosk-cta secondary" onClick={() => { setSent(false); setPhone(''); }}>Modifier mon numéro</button></div>
      : !mode ? <><p>Retrouve ta fidélité ou continue directement vers la carte.</p><div className="loyalty-entry-actions">
        <button type="button" className="kiosk-cta" onClick={() => setMode('login')}>Se connecter</button>
        <button type="button" className="kiosk-cta secondary" onClick={() => setMode('register')}>Créer un compte</button>
        <button type="button" className="kiosk-cta secondary" onClick={onContinue}>Commander sans compte</button>
      </div></> : <form onSubmit={event => { event.preventDefault(); if (phone.length === 10) setSent(true); }}>
        <h2>{mode === 'register' ? 'Créer un compte' : 'Se connecter'}</h2><label htmlFor="maythai-demo-phone">Ton numéro de téléphone</label>
        <input id="maythai-demo-phone" type="tel" inputMode="numeric" autoComplete="off" value={phone.replace(/(\d{2})(?=\d)/g, '$1 ')} maxLength={14} onChange={event => setPhone(event.target.value.replace(/\D/g, '').slice(0, 10))} />
        <NumericKeypad value={phone} maxLength={10} onChange={setPhone} />
        <p className="maythai-demo-note">La fidélité est simulée. Aucun numéro n’est transmis ni conservé.</p>
        <button type="submit" className="kiosk-cta" disabled={phone.length !== 10}>{mode === 'register' ? 'Recevoir mon formulaire par SMS' : 'Recevoir mon lien de connexion'}</button>
        <button type="button" className="kiosk-cta secondary" onClick={() => { setMode(''); setPhone(''); }}>Retour</button>
      </form>}
    {onClose && <button type="button" className="kiosk-cta secondary" onClick={onClose}>Fermer</button>}
  </div>;
}

export function MaythaiDemoReceipt({ order, onRestart }) {
  return <section className="maythai-demo-receipt" aria-label="Confirmation de ta commande de démonstration">
    <header><span>MAYTHAI · Démonstration</span><h1>Paiement accepté</h1><p>Ta commande est validée.</p></header>
    <strong className="maythai-demo-receipt-number">#{String(order.ticketNumber).padStart(4, '0')}</strong>
    <section className="maythai-demo-receipt-ticket" aria-label="Ton ticket de démonstration"><h2>Ton ticket</h2>
      <ol>{order.items.map((item, index) => <li key={index}><strong>{item.quantity} × {item.name}</strong>{item.details.length > 0 && <ul>{item.details.map((detail, detailIndex) => <li key={detailIndex}>{detail}</li>)}</ul>}</li>)}</ol>
      <p className="maythai-demo-receipt-total"><span>Total TTC</span><strong>{money(order.totalCents)}</strong></p>
      <p>{order.paymentMethod === 'cb' ? 'Carte bancaire' : 'Espèces'} · Paiement simulé</p>
    </section><p className="maythai-demo-note">Démonstration : aucun paiement, aucune préparation réelle.</p>
    <button type="button" className="kiosk-cta" onClick={onRestart}>Terminer</button>
  </section>;
}
